import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import ExtensionIcon from '@mui/icons-material/Extension';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getPlaceholders } from '../../services/chatbotflow.service';
import './HelperCustomField.scss';

/**
 * HelperCustomField (F7 Fase 1)
 *
 * Painel INLINE ("Adicionar valor de campo dinâmico?") que expande abaixo do
 * campo de texto do inspector. Nunca mostra na sidebar de props do JOINT direto —
 * é a UX escolhida pra descoberta gradual (usuário aciona só se quiser).
 *
 * 6 abas por categoria de placeholder (mesma fonte do PlaceholderPicker):
 *   Parâmetros, Variáveis, Campos, Chat, Sistema, Sessão.
 *
 * Signature original preservada (drop-in — 9 inspectors consomem sem alteração).
 * `hideChatTab` continua funcional pra retrocompat; adicionei `hideSystemTab`
 * e `hideSessionTab` seguindo o mesmo padrão. Prop `categories` (nova, override
 * completo) permite passar lista explícita quando o consumer quer controle fino.
 *
 * Fonte de dados: /api/chatbot-flow/get-placeholders (endpoint unificado).
 * Fetch lazy — dispara na primeira vez que o painel expande, resultado é
 * cacheado em state pro resto da sessão do inspector.
 *
 * Nomenclatura: "placeholder" (nunca "token" — token no projeto = JWT).
 */

interface Props {
  isRichText: boolean;
  description: string;
  param1: string[];
  onChangeDescription: Function;
  chatbotFlowId: string;
  hideChatTab?: boolean;
  hideSystemTab?: boolean;
  hideSessionTab?: boolean;
  /** Override completo — se fornecido, ignora os hideXTab. */
  categories?: string[];
}

type PlaceholderItem = {
  category: string;
  key: string;
  wrap: string;
  id?: number | null;
  label?: string;
};

const ALL_CATEGORIES = ['param', 'variable', 'customField', 'chat', 'system', 'session'];

const CATEGORY_META: Record<string, { label: string; Icon: any }> = {
  param:       { label: 'Parâmetros', Icon: ExtensionIcon },
  variable:    { label: 'Variáveis',  Icon: DataObjectIcon },
  customField: { label: 'Campos',      Icon: DashboardCustomizeIcon },
  chat:        { label: 'Chat',        Icon: PublicIcon },
  system:      { label: 'Sistema',     Icon: SettingsIcon },
  session:     { label: 'Sessão',      Icon: PersonIcon },
};

const HelperCustomField: React.FC<Props> = ({
  isRichText,
  description,
  param1,
  onChangeDescription,
  hideChatTab = false,
  hideSystemTab = false,
  hideSessionTab = false,
  categories,
}) => {
  const [showContainer, setShowContainer] = useState(false);
  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState('');

  const [items, setItems] = useState<PlaceholderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false); // evita re-fetch

  // Categorias efetivas (override > hideXTab > todas).
  const activeCategories = useMemo(() => {
    if (categories && categories.length > 0) {
      return ALL_CATEGORIES.filter(c => categories.includes(c));
    }
    const hidden = new Set<string>();
    if (hideChatTab) hidden.add('chat');
    if (hideSystemTab) hidden.add('system');
    if (hideSessionTab) hidden.add('session');
    return ALL_CATEGORIES.filter(c => !hidden.has(c));
  }, [categories, hideChatTab, hideSystemTab, hideSessionTab]);

  // Insere placeholder ao fim da descrição — mesma semantica histórica.
  const appendPlaceholder = useCallback(
    (placeholder: string) => {
      const cleaned = (description || '').replace(/^<p>/, '').replace(/<\/p>$/, '');
      const next = isRichText ? `<p>${cleaned} ${placeholder}</p>` : `${cleaned} ${placeholder}`;
      onChangeDescription(param1, next);
    },
    [description, isRichText, onChangeDescription, param1]
  );

  // Fetch on demand — só quando expandir a primeira vez.
  useEffect(() => {
    if (!showContainer || loaded) return;
    setLoading(true);
    (async () => {
      try {
        const res: any = await getPlaceholders();
        const raw: any[] = res?.data ?? res ?? [];
        const list: PlaceholderItem[] = (Array.isArray(raw) ? raw : []).map((it: any) => ({
          category: String(it.category ?? ''),
          key: String(it.key ?? ''),
          wrap: String(it.wrap ?? ''),
          id: it.id != null ? Number(it.id) : null,
          label: String(it.label ?? ''),
        }));
        setItems(list);
        setLoaded(true);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [showContainer, loaded]);

  // Reset ao fechar (mantém cache, só limpa busca/aba).
  useEffect(() => {
    if (!showContainer) {
      setQuery('');
      setTab(0);
    }
  }, [showContainer]);

  const currentCategory = activeCategories[tab] ?? activeCategories[0];
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter(it => it.category === currentCategory)
      .filter(it => !q
        || it.key.toLowerCase().includes(q)
        || (it.label ?? '').toLowerCase().includes(q)
        || it.wrap.toLowerCase().includes(q));
  }, [items, currentCategory, query]);

  return (
    <div className="box-container">
      <div style={{ marginBottom: '8px', marginTop: '8px' }}>
        <Switch
          value={showContainer}
          checked={showContainer}
          onChange={(e: any) => setShowContainer(e?.target?.checked)}
        />
        <label>Adicionar um valor de campo dinâmico?</label>
      </div>

      {showContainer && (
        <Box
          sx={{
            width: '100%',
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: 1,
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          <Tabs
            value={Math.min(tab, activeCategories.length - 1)}
            onChange={(_, v) => { setTab(v); setQuery(''); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 36, borderBottom: '1px solid rgba(0,0,0,0.08)' }}
          >
            {activeCategories.map(cat => {
              const meta = CATEGORY_META[cat];
              const Icon = meta.Icon;
              return (
                <Tab
                  key={cat}
                  icon={<Icon sx={{ fontSize: 14 }} />}
                  iconPosition="start"
                  label={meta.label}
                  sx={{ minHeight: 36, fontSize: 12 }}
                />
              );
            })}
          </Tabs>

          <Box sx={{ p: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ p: 1.5 }}>
                <Skeleton height={28} sx={{ mb: 0.5 }} />
                <Skeleton height={28} sx={{ mb: 0.5 }} />
                <Skeleton height={28} />
              </Box>
            ) : filteredItems.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">
                  {query ? 'Nada encontrado.' : 'Nenhum item nesta categoria.'}
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {filteredItems.map(item => (
                  <ListItem
                    key={`${item.category}-${item.key}-${item.id ?? ''}`}
                    secondaryAction={
                      <Tooltip title="Inserir no texto">
                        <IconButton edge="end" size="small" color="primary" onClick={() => appendPlaceholder(item.wrap)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                    onClick={() => appendPlaceholder(item.wrap)}
                  >
                    <ListItemText
                      primary={
                        <Chip
                          label={item.wrap}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace', fontSize: 11 }}
                        />
                      }
                      secondary={item.label ?? item.key}
                      secondaryTypographyProps={{ variant: 'caption', sx: { display: 'block', mt: 0.25 } }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      )}
    </div>
  );
};

export default HelperCustomField;
